import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

import { impact } from '@/telegram/haptics';

/** Раскадровка переворота из design-spec.md, доли от общих 460 мс. */
const FLIP_MS = 460;
const EDGE_MS = 230; // кадр ребра: здесь виброотклик и вспышка Клоуна
/**
 * Лицо влетает с ребра: карта уже развернулась к зрителю, осталась вторая
 * половина оборота. Первую половину играет уходящая рубашка.
 */
const KEYFRAMES = {
  rotateY: [88, -30, 0],
  scale: [1.06, 1.03, 1],
};
const TIMES = [0, 0.6, 1];

export interface FlipCardProps {
  back: ReactNode;
  face: ReactNode;
  flipped: boolean;
  onFlip: () => void;
  /** Вспышка цветом колоды на кадре ребра. Клоун поднимает головы всей комнате. */
  flash?: string;
}

/**
 * Переворот карты — единственный жест игры и единственное место, куда вынесена
 * вся эмоция интерфейса (design-spec.md, «Переворот карты»).
 *
 * Лицо монтируется только с началом переворота: до тапа текста задания в
 * разметке нет вовсе. Это не оптимизация — карточку читают через плечо.
 *
 * `prefers-reduced-motion` уважается: тогда карта просто меняет сторону.
 * Укачивать человека, который об этом попросил, ради зрелища нельзя.
 */
export function FlipCard({ back, face, flipped, onFlip, flash }: FlipCardProps) {
  const reduced = useReducedMotion();
  const [flashing, setFlashing] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
      pending.length = 0;
    };
  }, []);

  function handleFlip(): void {
    if (flipped) return;
    onFlip();
    if (reduced) return;

    // Отклик и вспышка привязаны к кадру ребра, а не к тапу: рука должна
    // почувствовать ровно тот момент, когда становится видно тип карточки.
    timers.current.push(
      setTimeout(() => {
        impact('medium');
        if (flash) {
          setFlashing(true);
          timers.current.push(setTimeout(() => setFlashing(false), 80));
        }
      }, EDGE_MS),
    );
  }

  return (
    <div className="relative" style={{ perspective: '1200px' }}>
      {/*
        В разметке всегда ровно одна сторона. Двух граней в общем слое быть не
        может: лицо тогда занимает высоту рубашки, и длинное задание обрезается
        по её нижней кромке — карта выглядит открытой не до конца.
      */}
      <motion.button
        key={flipped ? 'face' : 'back'}
        type="button"
        onClick={handleFlip}
        disabled={flipped}
        className="block w-full text-left"
        initial={flipped && !reduced ? { rotateY: 88, scale: 1.06 } : false}
        animate={flipped && !reduced ? KEYFRAMES : { rotateY: 0, scale: 1 }}
        transition={
          reduced
            ? { duration: 0 }
            : { duration: FLIP_MS / 1000, times: TIMES, ease: [0.2, 0.8, 0.2, 1] }
        }
      >
        {flipped ? face : back}
      </motion.button>

      {flashing && flash && (
        <motion.span
          aria-hidden
          className={`pointer-events-none fixed inset-0 z-10 ${flash}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.08 }}
        />
      )}
    </div>
  );
}
